import { Button } from '@/components/ui/button';
import { HelpTypePreview } from '@/nodes/HelpTypePreview';
import {
    InfoTypeDisplay,
    NodeBody,
    NodeContainer,
    NodeErrorIcon,
    NodeHeader,
    NodeOptionRows,
    NodePrompts,
} from '@/nodes/NodeUI';
import type { InputNodeType } from '@/nodes/types';
import { infoTypesByIdState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { GuideNodeId } from '@rock/types';
import { isSelectField, outgoingHandles } from '@rock/types';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { type FC, useContext } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';

const InputNode: FC<NodeProps<InputNodeType>> = ({ id, selected, data }) => {
    const paths = outgoingHandles.input(id as GuideNodeId, data);
    const { spaceId } = useContext(FlowParamsContext);

    const infoTypesById = useAsyncAtomValue(infoTypesByIdState(spaceId), {});
    const infoType = data.infoTypeId ? infoTypesById[data.infoTypeId] : undefined;
    // Same predicate the app switches rendering on, so the canvas shows the screen the author
    // will actually get: options instead of a field, and no continue button.
    const isSelect = !!infoType && isSelectField(infoType);

    return (
        <NodeContainer>
            <NodeErrorIcon id={id} />
            <NodeHeader id={id} type={data.type} title={data.pageTitle} />
            <NodeBody selected={selected}>
                <NodePrompts prompts={data.prompts} />
                <div className="py-1 px-3 gap-1 flex flex-row items-center justify-center">
                    <div className="text-xs text-primary">{data.dataKey} =</div>
                    <div className="bg-white border-1 border-black flex-1 py-1 px-4 gap-1 flex flex-row items-center justify-center">
                        <div className="text-xs text-primary">{isSelect ? 'Select:' : 'Input:'}</div>
                        {data.infoTypeId && <InfoTypeDisplay spaceId={spaceId} typeId={data.infoTypeId} />}
                    </div>
                </div>

                <div className="flex flex-col items-end px-3">
                    <HelpTypePreview helpType={data.helpType} spaceId={spaceId} modelNumberHelpEnabled={false} />
                </div>

                {isSelect ? (
                    <NodeOptionRows options={infoType.options ?? []} showNotSure={data.optional} />
                ) : (
                    <div className="flex flex-col items-center mb-2">
                        {data.optional && (
                            <Button size="sm" variant="link">
                                Skip
                            </Button>
                        )}
                        <Button size="sm">{data.buttonLabel || 'Button'}</Button>
                    </div>
                )}
            </NodeBody>

            <Handle type="target" position={Position.Left} />
            <Handle type="source" id={paths.next} position={Position.Right} />
        </NodeContainer>
    );
};

export default InputNode;
